import { useState } from 'react';
import { Pencil, Trash2, Star } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import { decodeChatPlainText } from '../utils/sanitize';
import { canEditOrDeleteOwnMessage } from '../utils/messagePolicy';
import type { ChatMessage } from '../types';

export interface ChatMessageListProps {
  messages: ChatMessage[];
  userId: string;
  /** Compliance: only tenant admins expand retained deletion payload. */
  canViewDeletionAudit: boolean;
  compact?: boolean;
  peerReadIdx?: number;
  starredMessageIds?: readonly string[];
  onToggleStar?: (messageId: string) => void;
  onEditOwn: (messageId: string, bodyRaw: string) => void | Promise<void>;
  onDeleteOwn: (messageId: string) => void | Promise<void>;
}

export function ChatMessageList({
  messages,
  userId,
  canViewDeletionAudit,
  compact,
  peerReadIdx = -1,
  starredMessageIds,
  onToggleStar,
  onEditOwn,
  onDeleteOwn,
}: ChatMessageListProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const bubblePad = compact ? 'px-2.5 py-1.5 text-sm' : 'px-3 py-2 text-sm';
  const bubbleMax = compact ? 'max-w-[88%]' : 'max-w-[85%]';

  const ease = 'duration-200 ease-[cubic-bezier(0.25,0.46,0.45,0.94)]';

  return (
    <>
      <div className={compact ? 'space-y-2.5 pr-1' : 'space-y-2.5 max-w-3xl mx-auto'}>
        {messages.map((m) => {
          const mine = m.authorId === userId;
          const starred = starredMessageIds?.includes(m.id);
          const deleted = Boolean(m.deletedAt);
          const canMutate = mine && !deleted && canEditOrDeleteOwnMessage(m, userId);
          const selfIdx = messages.findIndex((x) => x.id === m.id);
          const showReadReceipt = mine && Boolean(compact) && peerReadIdx >= selfIdx && selfIdx >= 0;
          const isEditing = editingId === m.id;

          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'} gap-2 group`}>
              <div
                className={`relative ${bubbleMax} rounded-2xl ${bubblePad} ring-1 shadow-sm transition-shadow ${ease} ${
                  mine
                    ? 'rounded-br-md bg-gradient-to-br from-cyber-cyan/92 to-cyber-blue/85 text-white ring-cyber-cyan/30 shadow-md shadow-black/20'
                    : 'rounded-bl-md border border-border/40 bg-card-bg/70 text-foreground ring-border/30'
                } ${deleted ? 'opacity-90' : ''}`}
              >
                {onToggleStar ? (
                  <button
                    type="button"
                    className={`absolute ${mine ? '-left-6' : '-right-6'} top-1 opacity-0 group-hover:opacity-100 transition-opacity ${ease}`}
                    onClick={() => onToggleStar(m.id)}
                    title="Star"
                  >
                    <Star className={`h-4 w-4 text-amber-400 ${starred ? 'fill-amber-400' : ''}`} />
                  </button>
                ) : null}

                {canMutate && !isEditing ? (
                  <div className={`absolute ${mine ? '-right-14' : '-left-14'} top-0 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ${ease}`}>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-foreground/90 hover:bg-background/20"
                      title="Edit (5 min)"
                      onClick={() => {
                        setEditingId(m.id);
                        setEditDraft(m.body);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-foreground/90 hover:bg-background/20"
                      title="Delete (5 min)"
                      onClick={() => setPendingDeleteId(m.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : null}

                {isEditing ? (
                  <div className="space-y-2 min-w-[200px]">
                    <Textarea
                      rows={compact ? 2 : 3}
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      className="resize-none bg-background/90 text-foreground text-sm"
                    />
                    <div className="flex justify-end gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => {
                          setEditingId(null);
                          setEditDraft('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => {
                          void onEditOwn(m.id, editDraft);
                          setEditingId(null);
                          setEditDraft('');
                        }}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                ) : deleted ? (
                  <p className="whitespace-pre-wrap break-words italic opacity-90">This message was deleted.</p>
                ) : (
                  <>
                    <p className="whitespace-pre-wrap break-words">{decodeChatPlainText(m.body)}</p>
                    {m.editedAt ? (
                      <p className={`text-[10px] mt-0.5 ${mine ? 'text-white/70' : 'text-muted-foreground'}`}>(edited)</p>
                    ) : null}
                  </>
                )}

                {!isEditing && !deleted
                  ? m.attachments?.map((a) =>
                      a.mime.startsWith('image/') && a.dataUrl ? (
                        compact ? (
                          <a key={a.id} href={a.dataUrl} download={a.name} className="mt-1 block">
                            <img src={a.dataUrl} alt={a.name} className="max-h-40 rounded-md border border-white/20" />
                          </a>
                        ) : (
                          <img
                            key={a.id}
                            src={a.dataUrl}
                            alt={a.name}
                            className="mt-2 max-h-56 rounded-md border border-white/20"
                          />
                        )
                      ) : a.dataUrl ? (
                        <a
                          key={a.id}
                          href={a.dataUrl}
                          download={a.name}
                          className="mt-1 block truncate text-xs underline opacity-90"
                        >
                          {a.name}
                        </a>
                      ) : (
                        <span key={a.id} className="mt-1 block truncate text-xs opacity-80">
                          {a.name}
                        </span>
                      ),
                    )
                  : null}

                {canViewDeletionAudit && m.deletionAudit ? (
                  <Collapsible className="mt-2 border-t border-white/20 pt-2 text-left">
                    <CollapsibleTrigger className="text-[10px] underline opacity-90 hover:opacity-100">
                      Admin: deletion audit
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2 space-y-1 rounded-md bg-black/25 p-2 text-[11px] leading-snug">
                      <p className="text-[10px] opacity-80">
                        Client-only demo: full audit text is stored on the device that performed the delete. Wire payloads omit
                        original content so peers cannot recover it; production should persist audits on the server for
                        tenant-wide admin review.
                      </p>
                      <p>
                        <span className="font-semibold">Deleted at:</span>{' '}
                        {new Date(m.deletionAudit.deletedAt).toLocaleString()}
                      </p>
                      <p>
                        <span className="font-semibold">Deleted by:</span> {m.deletionAudit.deletedBy}
                      </p>
                      {m.deletionAudit.editedSnapshotBeforeDelete ? (
                        <p>
                          <span className="font-semibold">Was edited before delete.</span>
                        </p>
                      ) : null}
                      <p className="whitespace-pre-wrap break-words">
                        <span className="font-semibold">Original:</span> {decodeChatPlainText(m.deletionAudit.originalBody)}
                      </p>
                      {m.deletionAudit.originalAttachments?.length ? (
                        <ul className="list-disc pl-4">
                          {m.deletionAudit.originalAttachments.map((a) => (
                            <li key={a.id}>
                              {a.name} ({a.mime})
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </CollapsibleContent>
                  </Collapsible>
                ) : null}

                <div
                  className={`mt-0.5 flex items-center justify-end gap-1 text-[10px] ${
                    mine ? 'text-white/75' : 'opacity-75'
                  }`}
                >
                  <span>
                    {compact
                      ? new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      : new Date(m.createdAt).toLocaleString()}
                  </span>
                  {mine && compact ? (
                    <span>{showReadReceipt ? 'Read' : m.delivery === 'sending' ? 'Sending…' : 'Sent'}</span>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <AlertDialog open={pendingDeleteId !== null} onOpenChange={(open: boolean) => !open && setPendingDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this message?</AlertDialogTitle>
            <AlertDialogDescription>
              Recipients will no longer see the content. Administrators can still open the compliance audit for this
              deletion when required.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDeleteId) void onDeleteOwn(pendingDeleteId);
                setPendingDeleteId(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
